/**
 * Admin Flight Management page — searchable flight list with filters and
 * modal-based amendment of any submitted flight record.
 * Route: #/admin/flights
 * Requirements: 17.1
 */

import { getUser, isAdmin } from '../services/auth.js';
import { getFlights, getPilots, getAircraft, updateFlight, transitionFlightStatus } from '../services/api.js';
import { navigate } from '../services/router.js';
import { formatDateUK, formatGBP, minsToHMM } from '../utils/format.js';
import { createDataGrid } from '../components/data-grid.js';
import { openDetailModal } from '../components/detail-modal.js';
import { FLIGHT_STATUS } from '../utils/constants.js';

// ---------------------------------------------------------------------------
// Pure helper functions (exported for testability)
// ---------------------------------------------------------------------------

/**
 * Filter flights by pilot, aircraft, and date range.
 *
 * @param {Object[]} flights - Array of flight records
 * @param {{ pilotId?: string, aircraftId?: string, dateFrom?: string, dateTo?: string }} filters
 * @returns {Object[]} Filtered flights
 */
export function filterFlights(flights, filters = {}) {
  if (!flights) return [];
  const { pilotId, aircraftId, dateFrom, dateTo, status } = filters;

  return flights.filter((f) => {
    if (pilotId && f.pilot_id !== pilotId) return false;
    if (aircraftId && f.aircraft_id !== aircraftId) return false;
    if (dateFrom && f.flight_date < dateFrom) return false;
    if (dateTo && f.flight_date > dateTo) return false;
    if (status && f.status !== status) return false;
    return true;
  });
}

/**
 * Format a flight record for display in the data grid.
 *
 * @param {Object} flight - Flight record with joined aircraft/pilots data
 * @returns {{ id: string, date: string, pilot: string, aircraft: string, route: string, flightTime: string, blockTime: string, billing: string }}
 */
export function formatFlightForDisplay(flight) {
  const pilotName = flight.captain_name || flight.pilots?.name || '—';
  const reg = flight.aircraft?.registration || '—';
  const type = flight.aircraft?.type || '';
  const aircraft = type ? `${reg} (${type})` : reg;
  const route = `${flight.departure || '?'} → ${flight.destination || '?'}`;

  return {
    id: flight.id,
    date: flight.flight_date ? formatDateUK(flight.flight_date) : '—',
    pilot: pilotName,
    aircraft,
    route,
    status: flight.status || '—',
    flightTime: minsToHMM(flight.flight_time_mins || 0),
    blockTime: minsToHMM(flight.block_time_mins || 0),
    billing: flight.who_pays || 'CAPTAIN',
  };
}

/**
 * Prepare a flight update payload from modal form data.
 * Converts string values to appropriate types.
 *
 * @param {Object} formData - Raw form data from the detail modal
 * @returns {Object} Cleaned payload suitable for the updateFlight API call
 */
export function prepareFlightUpdate(formData) {
  const update = {};

  if (formData.flight_date !== undefined && formData.flight_date !== '') {
    update.flight_date = formData.flight_date;
  }
  if (formData.captain_name !== undefined) {
    update.captain_name = formData.captain_name.trim() || null;
  }
  if (formData.departure !== undefined) {
    update.departure = formData.departure.trim();
  }
  if (formData.destination !== undefined) {
    update.destination = formData.destination.trim();
  }
  if (formData.departure_time !== undefined && formData.departure_time !== '') {
    update.departure_time = formData.departure_time;
  }
  if (formData.arrival_time !== undefined && formData.arrival_time !== '') {
    update.arrival_time = formData.arrival_time;
  }
  if (formData.block_time_mins !== undefined && formData.block_time_mins !== '') {
    update.block_time_mins = parseInt(formData.block_time_mins, 10);
  }
  if (formData.flight_time_mins !== undefined && formData.flight_time_mins !== '') {
    update.flight_time_mins = parseInt(formData.flight_time_mins, 10);
  }
  if (formData.ifr_time_mins !== undefined && formData.ifr_time_mins !== '') {
    update.ifr_time_mins = parseInt(formData.ifr_time_mins, 10);
  }
  if (formData.takeoffs !== undefined && formData.takeoffs !== '') {
    update.takeoffs = parseInt(formData.takeoffs, 10);
  }
  if (formData.landings !== undefined && formData.landings !== '') {
    update.landings = parseInt(formData.landings, 10);
  }
  if (formData.who_pays !== undefined && formData.who_pays !== '') {
    update.who_pays = formData.who_pays;
  }
  if (formData.notes !== undefined) {
    update.notes = formData.notes.trim() || null;
  }

  return update;
}

// ---------------------------------------------------------------------------
// Page render
// ---------------------------------------------------------------------------

/**
 * Render the admin flight management page.
 * Admin-only — redirects non-admin users.
 */
export async function renderAdminFlights() {
  const app = document.getElementById('app');
  if (!app) return;

  const user = getUser();
  if (!user) { navigate('/login'); return; }
  if (!isAdmin()) { navigate('/'); return; }


  app.innerHTML = `
    <div class="container">
      <h2>Flight Management</h2>
      <div id="admin-flights-msg"></div>

      <div class="card">
        <h3>Filters</h3>
        <form id="flight-filters" style="display:flex;gap:1rem;flex-wrap:wrap;align-items:end;">
          <div class="form-group" style="margin-bottom:0;">
            <label for="filter-pilot">Pilot</label>
            <select id="filter-pilot"><option value="">All Pilots</option></select>
          </div>
          <div class="form-group" style="margin-bottom:0;">
            <label for="filter-aircraft">Aircraft</label>
            <select id="filter-aircraft"><option value="">All Aircraft</option></select>
          </div>
          <div class="form-group" style="margin-bottom:0;">
            <label for="filter-status">Status</label>
            <select id="filter-status">
              <option value="">All Statuses</option>
              <option value="SUBMITTED">Submitted</option>
              <option value="APPROVED">Approved</option>
              <option value="FLOWN">Flown</option>
              <option value="REJECTED">Rejected</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
          </div>
          <div class="form-group" style="margin-bottom:0;">
            <label for="filter-date-from">From</label>
            <input type="date" id="filter-date-from" />
          </div>
          <div class="form-group" style="margin-bottom:0;">
            <label for="filter-date-to">To</label>
            <input type="date" id="filter-date-to" />
          </div>
          <button type="submit" class="btn btn--primary">Apply</button>
          <button type="button" id="clear-filters" class="btn">Clear</button>
        </form>
      </div>

      <div class="card">
        <h3>Flights</h3>
        <div id="flights-grid">Loading…</div>
      </div>
    </div>
  `;

  // Load reference data for filter dropdowns
  let allFlights = [];
  let pilots = [];
  let aircraft = [];

  try {
    [allFlights, pilots, aircraft] = await Promise.all([
      getFlights(),
      getPilots(),
      getAircraft(),
    ]);
  } catch (err) {
    showMsg('Failed to load data: ' + err.message, true);
    return;
  }

  // Populate pilot dropdown
  const pilotSelect = document.getElementById('filter-pilot');
  for (const p of pilots) {
    const opt = document.createElement('option');
    opt.value = p.id;
    opt.textContent = p.name;
    pilotSelect.appendChild(opt);
  }

  // Populate aircraft dropdown
  const aircraftSelect = document.getElementById('filter-aircraft');
  for (const a of aircraft) {
    const opt = document.createElement('option');
    opt.value = a.id;
    opt.textContent = `${a.registration} (${a.type})`;
    aircraftSelect.appendChild(opt);
  }

  // Render grid with current data
  function renderGrid(flights) {
    const gridContainer = document.getElementById('flights-grid');
    gridContainer.innerHTML = '';

    const displayData = flights.map(formatFlightForDisplay);

    const grid = createDataGrid({
      columns: [
        { key: 'date', label: 'Date' },
        { key: 'pilot', label: 'Pilot' },
        { key: 'aircraft', label: 'Aircraft' },
        { key: 'route', label: 'Route' },
        { key: 'status', label: 'Status' },
        { key: 'flightTime', label: 'Flight Time' },
        { key: 'blockTime', label: 'Block Time' },
        { key: 'billing', label: 'Who Pays' },
      ],
      data: displayData,
      defaultSort: 'date',
      defaultOrder: 'desc',
      emptyMessage: 'No flights found',
      actions: [
        {
          label: 'Approve',
          className: 'btn--success',
          show: (row) => row.status === FLIGHT_STATUS.SUBMITTED,
          onClick: async (row) => {
            try {
              await transitionFlightStatus(row.id, FLIGHT_STATUS.APPROVED);
              allFlights = await getFlights();
              renderGrid(filterFlights(allFlights, getCurrentFilters()));
              showMsg('Booking approved.', false);
            } catch (err) {
              showMsg('Approve failed: ' + err.message, true);
            }
          },
        },
        {
          label: 'Reject',
          className: 'btn--danger',
          show: (row) => row.status === FLIGHT_STATUS.SUBMITTED,
          onClick: (row) => {
            openRejectModal(row.id);
          },
        },
        {
          label: 'Amend',
          className: 'btn--primary',
          show: (row) => row.status === FLIGHT_STATUS.FLOWN,
          onClick: (row) => {
            const flight = flights.find((f) => f.id === row.id);
            if (flight) openAmendModal(flight);
          },
        },
      ],
    });

    gridContainer.appendChild(grid);
  }

  renderGrid(allFlights);

  const statusSelect = document.getElementById('filter-status');

  function getCurrentFilters() {
    return {
      pilotId: pilotSelect.value || undefined,
      aircraftId: aircraftSelect.value || undefined,
      dateFrom: document.getElementById('filter-date-from').value || undefined,
      dateTo: document.getElementById('filter-date-to').value || undefined,
      status: statusSelect.value || undefined,
    };
  }

  // Filter form
  document.getElementById('flight-filters').addEventListener('submit', (e) => {
    e.preventDefault();
    renderGrid(filterFlights(allFlights, getCurrentFilters()));
  });

  // Clear filters
  document.getElementById('clear-filters').addEventListener('click', () => {
    pilotSelect.value = '';
    aircraftSelect.value = '';
    statusSelect.value = '';
    document.getElementById('filter-date-from').value = '';
    document.getElementById('filter-date-to').value = '';
    renderGrid(allFlights);
  });

  /**
   * Open the amendment modal for a flight record.
   */
  function openAmendModal(flight) {
    openDetailModal({
      title: `Amend Flight — ${flight.flight_date}`,
      fields: [
        { name: 'flight_date', label: 'Flight Date', type: 'date', required: true },
        { name: 'captain_name', label: 'Captain', type: 'text' },
        { name: 'departure', label: 'Departure', type: 'text', required: true },
        { name: 'destination', label: 'Destination', type: 'text', required: true },
        { name: 'departure_time', label: 'Departure Time', type: 'time' },
        { name: 'arrival_time', label: 'Arrival Time', type: 'time' },
        { name: 'block_time_mins', label: 'Block Time (mins)', type: 'number' },
        { name: 'flight_time_mins', label: 'Flight Time (mins)', type: 'number' },
        { name: 'ifr_time_mins', label: 'IFR Time (mins)', type: 'number' },
        { name: 'takeoffs', label: 'Takeoffs', type: 'number' },
        { name: 'landings', label: 'Landings', type: 'number' },
        {
          name: 'who_pays',
          label: 'Who Pays',
          type: 'select',
          options: [
            { value: 'CAPTAIN', label: 'Captain' },
            { value: 'P2', label: 'P2' },
          ],
        },
        { name: 'notes', label: 'Notes', type: 'textarea' },
      ],
      values: {
        flight_date: flight.flight_date || '',
        captain_name: flight.captain_name || '',
        departure: flight.departure || '',
        destination: flight.destination || '',
        departure_time: flight.departure_time || '',
        arrival_time: flight.arrival_time || '',
        block_time_mins: flight.block_time_mins != null ? String(flight.block_time_mins) : '',
        flight_time_mins: flight.flight_time_mins != null ? String(flight.flight_time_mins) : '',
        ifr_time_mins: flight.ifr_time_mins != null ? String(flight.ifr_time_mins) : '',
        takeoffs: flight.takeoffs != null ? String(flight.takeoffs) : '',
        landings: flight.landings != null ? String(flight.landings) : '',
        who_pays: flight.who_pays || 'CAPTAIN',
        notes: flight.notes || '',
      },
      onSave: async (formData) => {
        const payload = prepareFlightUpdate(formData);
        await updateFlight(flight.id, payload);
        allFlights = await getFlights();
        renderGrid(filterFlights(allFlights, getCurrentFilters()));
        showMsg('Flight updated successfully.', false);
      },
    });
  }

  /**
   * Open a modal to reject a booking with a required reason.
   */
  function openRejectModal(flightId) {
    openDetailModal({
      title: 'Reject Booking',
      fields: [
        { name: 'reject_reason', label: 'Reason for Rejection', type: 'textarea', required: true },
      ],
      values: { reject_reason: '' },
      saveLabel: 'Reject',
      onSave: async (formData) => {
        const reason = (formData.reject_reason || '').trim();
        if (!reason) throw new Error('Rejection reason is required');
        await transitionFlightStatus(flightId, FLIGHT_STATUS.REJECTED, { reject_reason: reason });
        allFlights = await getFlights();
        renderGrid(filterFlights(allFlights, getCurrentFilters()));
        showMsg('Booking rejected.', false);
      },
    });
  }

  function showMsg(text, isError) {
    const el = document.getElementById('admin-flights-msg');
    if (!el) return;
    el.textContent = text;
    el.className = isError ? 'msg msg--error' : 'msg msg--success';
    setTimeout(() => { el.textContent = ''; el.className = ''; }, 4000);
  }
}
